import { Component } from 'react';
import {
  Button,
  Dialog,
  DialogTrigger,
  Group,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  SearchField,
  Select,
  SelectValue,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { Formik, type FormikProps } from 'formik';
import { clone, get } from 'lodash';

import { Alert, CopyToClipboard, type RequestError } from '../../shared';
import { type Account, AccountRole, type Member, type OwnerPromotion } from '../model';

import './owner.scss';

export type OwnerFormProps = {
  account: Account;
  members: Member[];
  isUpdatingAccountOwner: boolean;
  accountOwnerUpdateError: RequestError | null;
  onChangeAccountOwner: (promotion: OwnerPromotion) => void;
};

type OwnerFormState = Readonly<{
  isConfirmationOpen: boolean;
  isSuccessMessageVisible: boolean;
  lastOwnerChange: OwnerPromotion | null;
  previousOwnerEmail: string;
  wasUpdating: boolean;
  promotedEmailSearch: string;
  requestedBySearch: string;
  isOwnerSelectorOpen: boolean;
  isRequesterSelectorOpen: boolean;
  filteredOwnerItems: Array<{ id: string; name: string }>;
  filteredRequesterItems: Array<{ id: string; name: string }>;
}>;

export class OwnerForm extends Component<OwnerFormProps, OwnerFormState> {
  public state: OwnerFormState = {
    isConfirmationOpen: false,
    isSuccessMessageVisible: false,
    lastOwnerChange: null,
    previousOwnerEmail: '',
    wasUpdating: false,
    promotedEmailSearch: '',
    requestedBySearch: '',
    isOwnerSelectorOpen: false,
    isRequesterSelectorOpen: false,
    filteredOwnerItems: [],
    filteredRequesterItems: [],
  };

  public componentDidMount() {
    // Initialize filtered items
    this.updateFilteredItems();
  }

  public componentDidUpdate(prevProps: OwnerFormProps, _prevState: OwnerFormState) {
    // Track when updating starts
    if (!prevProps.isUpdatingAccountOwner && this.props.isUpdatingAccountOwner) {
      this.setState({ wasUpdating: true });
    }

    // Show success message only when the update completes successfully
    if (
      prevProps.isUpdatingAccountOwner &&
      !this.props.isUpdatingAccountOwner &&
      this.state.wasUpdating &&
      this.state.lastOwnerChange &&
      this.props.accountOwnerUpdateError === null
    ) {
      this.setState({
        isSuccessMessageVisible: true,
        wasUpdating: false,
      });
    }

    // Reset wasUpdating if there was an error
    if (
      prevProps.isUpdatingAccountOwner &&
      !this.props.isUpdatingAccountOwner &&
      this.state.wasUpdating &&
      this.props.accountOwnerUpdateError !== null
    ) {
      this.setState({
        wasUpdating: false,
      });
    }

    // Update filtered items when search terms or members change
    if (
      _prevState.promotedEmailSearch !== this.state.promotedEmailSearch ||
      _prevState.requestedBySearch !== this.state.requestedBySearch ||
      prevProps.members !== this.props.members
    ) {
      this.updateFilteredItems();
    }
  }

  private getOwnerMemberItems = () => {
    const { members } = this.props;
    const owner = this.getOwner(members);

    return members
      .filter((member) => {
        if (owner && member.email === owner.email) {
          return false;
        }
        return true;
      })
      .map((member) => ({
        id: member.email,
        name: member.email,
      }));
  };

  private getRequesterMemberItems = () => {
    const { members } = this.props;

    return members.map((member) => ({
      id: member.email,
      name: member.email,
    }));
  };

  private updateFilteredItems = () => {
    const { promotedEmailSearch, requestedBySearch } = this.state;

    // Get base items
    const allOwnerItems = this.getOwnerMemberItems();
    const allRequesterItems = this.getRequesterMemberItems();

    // Filter owner items
    const filteredOwnerItems = promotedEmailSearch
      ? allOwnerItems.filter((item) => item.name.toLowerCase().includes(promotedEmailSearch.toLowerCase()))
      : allOwnerItems;

    // Filter requester items
    const filteredRequesterItems = requestedBySearch
      ? allRequesterItems.filter((item) => item.name.toLowerCase().includes(requestedBySearch.toLowerCase()))
      : allRequesterItems;

    this.setState({
      filteredOwnerItems,
      filteredRequesterItems,
    });
  };

  private handleOwnerInputChange = (value: string) => {
    // Update input value immediately for responsive UI
    this.setState({ promotedEmailSearch: value });
  };

  private handleRequesterInputChange = (value: string) => {
    // Update input value immediately for responsive UI
    this.setState({ requestedBySearch: value });
  };

  private handleOwnerClear = () => {
    this.setState({
      promotedEmailSearch: '',
    });
  };

  private handleRequesterClear = () => {
    this.setState({
      requestedBySearch: '',
    });
  };

  private getOwner = (members: Member[]) => members.find((m) => m.role === AccountRole.OWNER);

  public render() {
    const {
      isSuccessMessageVisible,
      lastOwnerChange,
      previousOwnerEmail,
      promotedEmailSearch,
      requestedBySearch,
      isOwnerSelectorOpen,
      isRequesterSelectorOpen,
      filteredOwnerItems,
      filteredRequesterItems,
    } = this.state;

    const owner = this.getOwner(this.props.members);
    if (!owner) {
      return null;
    }

    const confirmationText = lastOwnerChange
      ? `new owner: ${lastOwnerChange.promotedEmail}, requested by: ${lastOwnerChange.requestedBy}, previous owner: ${previousOwnerEmail}, demoted role: ${lastOwnerChange.demotedRole}`
      : '';

    return (
      <>
        <Formik
          enableReinitialize
          initialValues={{
            promotedEmail: owner.email,
            requestedBy: '',
            demotedRole: AccountRole.ADMIN,
          }}
          onSubmit={(values: OwnerPromotion) =>
            this.setState({
              isConfirmationOpen: true,
              lastOwnerChange: values,
              previousOwnerEmail: owner.email,
            })
          }
          component={(form: FormikProps<OwnerPromotion>) => (
            <form onSubmit={form.handleSubmit} className="OwnerForm">
              <div className="OwnerForm-field OwnerForm-field--searchable">
                <Group className="OwnerForm-field__group">
                  <Label>Account owner:</Label>
                  <DialogTrigger>
                    <Button
                      className="OwnerForm-select-button"
                      onPress={() =>
                        this.setState({
                          isOwnerSelectorOpen: true,
                          promotedEmailSearch: '',
                        })
                      }
                    >
                      <Group>
                        {form.values.promotedEmail || 'Select member...'}
                        <Icon name="chevron-down" size="small" />
                      </Group>
                    </Button>
                    <Popover
                      placement="bottom start"
                      isOpen={isOwnerSelectorOpen}
                      onOpenChange={(open) => {
                        this.setState({
                          isOwnerSelectorOpen: open,
                          promotedEmailSearch: open ? '' : promotedEmailSearch,
                        });
                      }}
                    >
                      <Dialog aria-label="Search for account owner">
                        <div className="OwnerForm-search-container">
                          <div className="OwnerForm-search-with-clear">
                            <SearchField aria-label="search for account owner" autoFocus>
                              <Group>
                                <Icon name="search" size="small" />
                                <Input
                                  placeholder="Search for member..."
                                  value={promotedEmailSearch}
                                  onChange={(e) => this.handleOwnerInputChange(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      this.setState({
                                        isOwnerSelectorOpen: false,
                                      });
                                      this.handleOwnerClear();
                                    }
                                  }}
                                />
                              </Group>
                            </SearchField>
                            {promotedEmailSearch && (
                              <Button
                                variant="minimal"
                                size="small"
                                aria-label="Clear search"
                                onPress={this.handleOwnerClear}
                              >
                                <Icon name="cancel" size="small" />
                              </Button>
                            )}
                          </div>
                          <ListBox
                            selectionMode="single"
                            aria-label="Account owner options"
                            onSelectionChange={(keys) => {
                              const selectedKey = Array.from(keys)[0] as string;
                              if (selectedKey && selectedKey !== 'no-results') {
                                form.setFieldValue('promotedEmail', selectedKey);
                                this.setState({
                                  promotedEmailSearch: '',
                                  isOwnerSelectorOpen: false,
                                });
                              }
                            }}
                          >
                            {filteredOwnerItems.map((item) => (
                              <ListBoxItem key={`owner-${item.id}`} id={item.id} textValue={item.name}>
                                {item.name}
                              </ListBoxItem>
                            ))}
                            {filteredOwnerItems.length === 0 && promotedEmailSearch.length > 0 && (
                              <ListBoxItem key="owner-no-results" id="no-results" textValue="No members found">
                                <div className="OwnerForm-no-results">No members found</div>
                              </ListBoxItem>
                            )}
                          </ListBox>
                        </div>
                      </Dialog>
                    </Popover>
                  </DialogTrigger>
                </Group>
                {this.getFieldHelperText(form, 'promotedEmail') && (
                  <div className="OwnerForm-helper-text">{this.getFieldHelperText(form, 'promotedEmail')}</div>
                )}
              </div>

              <div className="OwnerForm-field OwnerForm-field--searchable">
                <Group className="OwnerForm-field__group">
                  <Label>Requested by:</Label>
                  <DialogTrigger>
                    <Button
                      className="OwnerForm-select-button"
                      onPress={() =>
                        this.setState({
                          isRequesterSelectorOpen: true,
                          requestedBySearch: '',
                        })
                      }
                    >
                      <Group>
                        {form.values.requestedBy || 'Select member...'}
                        <Icon name="chevron-down" size="small" />
                      </Group>
                    </Button>
                    <Popover
                      placement="bottom start"
                      isOpen={isRequesterSelectorOpen}
                      onOpenChange={(open) => {
                        this.setState({
                          isRequesterSelectorOpen: open,
                          requestedBySearch: open ? '' : requestedBySearch,
                        });
                      }}
                    >
                      <Dialog aria-label="Search for who requested the change">
                        <div className="OwnerForm-search-container">
                          <div className="OwnerForm-search-with-clear">
                            <SearchField aria-label="search for who requested the change" autoFocus>
                              <Group>
                                <Icon name="search" size="small" />
                                <Input
                                  placeholder="Search for member..."
                                  value={requestedBySearch}
                                  onChange={(e) => this.handleRequesterInputChange(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      this.setState({
                                        isRequesterSelectorOpen: false,
                                      });
                                      this.handleRequesterClear();
                                    }
                                  }}
                                />
                              </Group>
                            </SearchField>
                            {requestedBySearch && (
                              <Button
                                variant="minimal"
                                size="small"
                                aria-label="Clear search"
                                onPress={this.handleRequesterClear}
                              >
                                <Icon name="cancel" size="small" />
                              </Button>
                            )}
                          </div>
                          <ListBox
                            selectionMode="single"
                            aria-label="Requested by options"
                            onSelectionChange={(keys) => {
                              const selectedKey = Array.from(keys)[0] as string;
                              if (selectedKey && selectedKey !== 'no-results') {
                                form.setFieldValue('requestedBy', selectedKey);
                                this.setState({
                                  requestedBySearch: '',
                                  isRequesterSelectorOpen: false,
                                });
                              }
                            }}
                          >
                            {filteredRequesterItems.map((item) => (
                              <ListBoxItem key={`requester-${item.id}`} id={item.id} textValue={item.name}>
                                {item.name}
                              </ListBoxItem>
                            ))}
                            {filteredRequesterItems.length === 0 && requestedBySearch.length > 0 && (
                              <ListBoxItem key="requester-no-results" id="no-results" textValue="No members found">
                                <div className="OwnerForm-no-results">No members found</div>
                              </ListBoxItem>
                            )}
                          </ListBox>
                        </div>
                      </Dialog>
                    </Popover>
                  </DialogTrigger>
                </Group>
                {this.getFieldHelperText(form, 'requestedBy') && (
                  <div className="OwnerForm-helper-text">{this.getFieldHelperText(form, 'requestedBy')}</div>
                )}
              </div>

              <div className="OwnerForm-field">
                <Group className="OwnerForm-field__group">
                  <Label>Demoted role:</Label>
                  <Select
                    selectedKey={form.values.demotedRole}
                    onSelectionChange={(key: string | number | null) =>
                      form.setFieldValue('demotedRole', key as AccountRole)
                    }
                    aria-label="Select demoted role"
                  >
                    <Button>
                      <SelectValue />
                      <Icon name="chevron-down" size="small" slot="end-icon" />
                    </Button>
                    <Popover>
                      <ListBox>
                        {[AccountRole.ADMIN, AccountRole.WRITER, AccountRole.READER].map((role) => (
                          <ListBoxItem key={role} id={role} textValue={role}>
                            {role}
                          </ListBoxItem>
                        ))}
                      </ListBox>
                    </Popover>
                  </Select>
                </Group>
                {this.getFieldHelperText(form, 'demotedRole') && (
                  <div className="OwnerForm-helper-text">{this.getFieldHelperText(form, 'demotedRole')}</div>
                )}
              </div>

              <Button
                variant="primary"
                type="submit"
                isDisabled={this.props.isUpdatingAccountOwner || this.ownerEmailUnchanged(form)}
              >
                {this.props.isUpdatingAccountOwner ? 'Saving account owner…' : 'Save'}
              </Button>

              {isSuccessMessageVisible && (
                <div className="OwnerForm-success-message">
                  <div className="OwnerForm-success-message__header">
                    <span className="OwnerForm-success-message__header-text">✓ Owner changed successfully!</span>
                    <CopyToClipboard content={confirmationText}>
                      <Button size="small" variant="minimal" aria-label="Copy details">
                        📋
                      </Button>
                    </CopyToClipboard>
                  </div>
                  <ul className="OwnerForm-success-message__details">
                    <li>
                      <strong>New owner:</strong> {lastOwnerChange?.promotedEmail}
                    </li>
                    <li>
                      <strong>Requested by:</strong> {lastOwnerChange?.requestedBy}
                    </li>
                    <li>
                      <strong>Previous owner:</strong> {previousOwnerEmail}
                    </li>
                    <li>
                      <strong>Demoted role:</strong> {lastOwnerChange?.demotedRole}
                    </li>
                  </ul>
                </div>
              )}

              <Alert
                intent="danger"
                isOpen={this.state.isConfirmationOpen}
                onClose={() => this.setState({ isConfirmationOpen: false })}
                onConfirm={() => this.handleSubmit(this.state.lastOwnerChange!)}
                onCancel={() => this.setState({ isConfirmationOpen: false })}
                confirmButtonText="Confirm"
                cancelButtonText="Cancel"
                title="Confirm Owner Change"
              >
                Are you sure you want to change the account owner?
              </Alert>
            </form>
          )}
        />
      </>
    );
  }

  public handleSubmit = (promotion: OwnerPromotion) => {
    const payload = clone(promotion);
    this.props.onChangeAccountOwner(payload);
    this.setState({
      isConfirmationOpen: false,
    });
  };

  private hasError = (form: FormikProps<OwnerPromotion>, name: string) =>
    get(form.touched, name, false) && get(form.errors, name, null);

  private getFieldHelperText = (form: FormikProps<OwnerPromotion>, name: string) =>
    this.hasError(form, name) ? get(form.errors, name) : undefined;

  private ownerEmailUnchanged = (form: FormikProps<OwnerPromotion>) =>
    form.values.promotedEmail === form.initialValues.promotedEmail;
}
